import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import {
  BarChart3, Calendar, TrendingUp, AlertTriangle, Users,
  Zap, Clock, ArrowUp, ArrowDown, Loader2, Factory,
} from 'lucide-react';

const INTENSITY_COLORS = ['#f1f5f9', '#dbeafe', '#93c5fd', '#3b82f6', '#1e40af'];
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function ForecastingPage() {
  const [demandCalendar, setDemandCalendar] = useState([]);
  const [capacityData, setCapacityData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [whatIfResult, setWhatIfResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatIfLoading, setWhatIfLoading] = useState(false);

  // What-if scenario inputs
  const [tempWorkers, setTempWorkers] = useState(0);
  const [outsourcingIncrease, setOutsourcingIncrease] = useState(0);
  const [shiftExtension, setShiftExtension] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [calendarRes, capacityRes, alertsRes] = await Promise.all([
        api.get('/forecasting/demand-calendar?weeks_ahead=8'),
        api.get('/forecasting/capacity-vs-demand?weeks_ahead=8'),
        api.get('/forecasting/booking-alerts'),
      ]);
      setDemandCalendar(calendarRes.data?.calendar || []);
      setCapacityData(capacityRes.data);
      setAlerts(alertsRes.data?.alerts || []);
    } catch (err) {
      console.error('Failed to fetch forecasting data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runWhatIf = async () => {
    setWhatIfLoading(true);
    try {
      const res = await api.post('/forecasting/what-if?weeks_ahead=8', {
        add_temp_workers: tempWorkers,
        increase_outsourcing_pct: outsourcingIncrease,
        extend_shift_hours: shiftExtension,
      });
      setWhatIfResult(res.data);
    } catch (err) {
      console.error('What-if calculation failed:', err);
    } finally {
      setWhatIfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  // Group calendar by weeks
  const calendarWeeks = [];
  for (let i = 0; i < demandCalendar.length; i += 7) {
    calendarWeeks.push(demandCalendar.slice(i, i + 7));
  }

  const weeks = capacityData?.weeks || [];
  const summary = capacityData?.summary || {};
  const maxCapacity = Math.max(...weeks.map(w => Math.max(w.capacity, w.demand)), 1);

  return (
    <div className="space-y-6" data-testid="forecasting-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold flex items-center gap-3" style={{ color: 'var(--brand-navy)' }}>
          <BarChart3 className="w-8 h-8" style={{ color: 'var(--brand-gold)' }} />
          Production Forecasting
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>
          Capacity planning and demand analysis
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Users className="w-4 h-4" />
              Workers
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: 'var(--brand-navy)' }}>
              {summary.total_workers || 0}
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Clock className="w-4 h-4" />
              Daily Hours
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: 'var(--brand-navy)' }}>
              {summary.daily_capacity_hours || 0}h
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <Factory className="w-4 h-4" />
              Weekly Capacity
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: '#1E7A5F' }}>
              {summary.weekly_capacity_garments || 0}
            </div>
            <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>garments</span>
          </CardContent>
        </Card>
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              <TrendingUp className="w-4 h-4" />
              Avg Utilization
            </div>
            <div className="text-2xl font-bold mt-1" style={{
              color: summary.avg_utilization > 100 ? '#EF4444' : summary.avg_utilization > 80 ? '#F59E0B' : '#22C55E'
            }}>
              {summary.avg_utilization || 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="rounded-xl border border-red-200 bg-red-50 shadow-sm" data-testid="booking-alerts">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Booking Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.map(alert => (
                <div key={alert.week_start} className="p-3 rounded-lg bg-white border border-red-200">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm" style={{ color: 'var(--brand-navy)' }}>
                      {alert.week_label} ({alert.week_start})
                    </span>
                    <Badge variant="destructive" className="text-xs">
                      {alert.severity === 'critical' ? 'OVERBOOKED' : 'WARNING'}
                    </Badge>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span style={{ color: 'var(--ink-muted)' }}>Utilization:</span>
                      <span className="ml-1 font-semibold text-red-600">{alert.utilization_pct}%</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--ink-muted)' }}>Demand:</span>
                      <span className="ml-1 font-semibold">{alert.demand_garments} garments</span>
                    </div>
                    <div>
                      <span style={{ color: 'var(--ink-muted)' }}>Express:</span>
                      <span className="ml-1 font-semibold text-amber-600">{alert.express_orders}</span>
                    </div>
                  </div>
                  {alert.excess_garments > 0 && (
                    <div className="mt-2 text-xs text-red-600 font-medium">
                      {alert.excess_garments} garments over capacity - consider outsourcing or rescheduling
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Demand Calendar Heatmap */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="demand-calendar">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Calendar className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
              Demand Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Day labels */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAY_NAMES.map(day => (
                <div key={day} className="text-xs font-medium text-center" style={{ color: 'var(--ink-muted)' }}>
                  {day}
                </div>
              ))}
            </div>
            {/* Calendar grid */}
            <div className="space-y-1">
              {calendarWeeks.map((week, weekIdx) => (
                <div key={weekIdx} className="grid grid-cols-7 gap-1">
                  {week.map((day, dayIdx) => (
                    <div key={day.date} className="relative group">
                      <div
                        className="aspect-square rounded flex items-center justify-center text-xs font-medium transition-all cursor-pointer hover:ring-2 hover:ring-offset-1"
                        style={{
                          background: INTENSITY_COLORS[day.intensity],
                          color: day.intensity > 2 ? 'white' : 'var(--brand-navy)',
                          ringColor: 'var(--brand-gold)',
                        }}
                        title={`${day.date}: ${day.garments} garments, ${day.orders} orders`}
                      >
                        {new Date(day.date).getDate()}
                      </div>
                      {/* Tooltip */}
                      <div className="absolute z-10 hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap">
                        {day.garments} garments • {day.orders} orders
                        {day.express > 0 && <span className="text-amber-300"> • {day.express} express</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {/* Legend */}
            <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t">
              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Less</span>
              {INTENSITY_COLORS.map((color, idx) => (
                <div key={idx} className="w-4 h-4 rounded" style={{ background: color }} />
              ))}
              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>More</span>
            </div>
          </CardContent>
        </Card>

        {/* Capacity vs Demand */}
        <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="capacity-demand">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
              Capacity vs Demand
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {weeks.map((week, idx) => (
                <div key={week.week_start} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium" style={{ color: 'var(--brand-navy)' }}>
                      Week {idx + 1}
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs" style={{
                        background: week.status === 'overbooked' ? 'rgba(239,68,68,0.10)' : week.status === 'at_capacity' ? 'rgba(245,158,11,0.10)' : 'rgba(34,197,94,0.10)',
                        color: week.status === 'overbooked' ? '#EF4444' : week.status === 'at_capacity' ? '#F59E0B' : '#22C55E',
                        borderColor: 'transparent',
                      }}>
                        {week.utilization_pct}%
                      </Badge>
                      {week.express_demand > 0 && (
                        <Badge className="text-xs gap-1" style={{ background: 'rgba(245,158,11,0.10)', color: '#F59E0B', border: 'none' }}>
                          <Zap className="w-3 h-3" /> {week.express_demand}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="relative h-6 rounded overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                    {/* Capacity bar */}
                    <div className="absolute inset-y-0 left-0 rounded-l" style={{
                      width: `${(week.capacity / maxCapacity) * 100}%`,
                      background: 'rgba(27,42,74,0.15)',
                    }} />
                    {/* Demand bar */}
                    <div className="absolute inset-y-0 left-0 rounded-l" style={{
                      width: `${(week.demand / maxCapacity) * 100}%`,
                      background: week.demand > week.capacity
                        ? 'linear-gradient(90deg, #EF4444, #F87171)'
                        : week.demand > week.capacity * 0.8
                        ? 'linear-gradient(90deg, #F59E0B, #FBBF24)'
                        : 'linear-gradient(90deg, #22C55E, #4ADE80)',
                    }} />
                    {/* Labels */}
                    <div className="absolute inset-0 flex items-center justify-between px-2 text-xs font-medium">
                      <span style={{ color: week.demand > 2 ? 'white' : 'var(--brand-navy)' }}>
                        {week.demand} garments
                      </span>
                      <span style={{ color: 'var(--ink-muted)' }}>
                        / {week.capacity}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* What-If Scenario Panel */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="what-if-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Zap className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
            What-If Scenario Planner
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Inputs */}
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-medium flex items-center justify-between">
                  <span>Add Temporary Workers</span>
                  <span className="font-bold" style={{ color: 'var(--brand-gold)' }}>+{tempWorkers}</span>
                </Label>
                <Slider
                  value={[tempWorkers]}
                  onValueChange={([val]) => setTempWorkers(val)}
                  max={5}
                  step={1}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-xs font-medium flex items-center justify-between">
                  <span>Increase Outsourcing</span>
                  <span className="font-bold" style={{ color: 'var(--brand-gold)' }}>+{outsourcingIncrease}%</span>
                </Label>
                <Slider
                  value={[outsourcingIncrease]}
                  onValueChange={([val]) => setOutsourcingIncrease(val)}
                  max={100}
                  step={10}
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-xs font-medium flex items-center justify-between">
                  <span>Extend Shift (hours/day)</span>
                  <span className="font-bold" style={{ color: 'var(--brand-gold)' }}>+{shiftExtension}h</span>
                </Label>
                <Slider
                  value={[shiftExtension]}
                  onValueChange={([val]) => setShiftExtension(val)}
                  max={4}
                  step={0.5}
                  className="mt-2"
                />
              </div>
              <Button onClick={runWhatIf} disabled={whatIfLoading} className="w-full gap-2" style={{ background: 'var(--brand-navy)' }}>
                {whatIfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                Calculate Impact
              </Button>
            </div>

            {/* Results */}
            {whatIfResult && (
              <>
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Baseline</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded" style={{ background: 'var(--surface-2)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>Workers</span>
                      <div className="font-semibold">{whatIfResult.baseline.workers}</div>
                    </div>
                    <div className="p-2 rounded" style={{ background: 'var(--surface-2)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>Daily Hours</span>
                      <div className="font-semibold">{whatIfResult.baseline.daily_hours}h</div>
                    </div>
                    <div className="p-2 rounded col-span-2" style={{ background: 'var(--surface-2)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>Weekly Capacity</span>
                      <div className="font-semibold">{whatIfResult.baseline.weekly_capacity} garments</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="text-sm font-semibold flex items-center gap-2" style={{ color: '#1E7A5F' }}>
                    Projected
                    <Badge className="text-xs" style={{ background: 'rgba(34,197,94,0.10)', color: '#22C55E', border: 'none' }}>
                      <ArrowUp className="w-3 h-3 mr-1" />
                      {whatIfResult.impact.capacity_increase_pct}%
                    </Badge>
                  </h4>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded" style={{ background: 'rgba(34,197,94,0.10)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>Workers</span>
                      <div className="font-semibold" style={{ color: '#1E7A5F' }}>{whatIfResult.projected.workers}</div>
                    </div>
                    <div className="p-2 rounded" style={{ background: 'rgba(34,197,94,0.10)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>Daily Hours</span>
                      <div className="font-semibold" style={{ color: '#1E7A5F' }}>{whatIfResult.projected.daily_hours}h</div>
                    </div>
                    <div className="p-2 rounded" style={{ background: 'rgba(34,197,94,0.10)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>In-house</span>
                      <div className="font-semibold" style={{ color: '#1E7A5F' }}>{whatIfResult.projected.weekly_capacity}</div>
                    </div>
                    <div className="p-2 rounded" style={{ background: 'rgba(34,197,94,0.10)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>Outsourced</span>
                      <div className="font-semibold" style={{ color: '#1E7A5F' }}>{whatIfResult.projected.outsourcing_capacity}</div>
                    </div>
                    <div className="p-2 rounded col-span-2 border border-green-200" style={{ background: 'rgba(34,197,94,0.15)' }}>
                      <span style={{ color: 'var(--ink-muted)' }}>Total Weekly Capacity</span>
                      <div className="text-lg font-bold" style={{ color: '#1E7A5F' }}>
                        {whatIfResult.projected.total_weekly_capacity} garments
                      </div>
                      <div className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                        Backlog ({whatIfResult.impact.current_backlog}) cleared in ~{whatIfResult.impact.weeks_to_clear_backlog} weeks
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
